import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Card, Heading, Icon, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { CashAccountOverview } from './cash-account-overview/CashAccountOverview';
import { DirectTradingAccountOverview } from './direct-trading-account-overview/DirectTradingAccountOverview';
import { EnvelopeAccountHeaderDocument } from './graphql/envelopeAccountHeader.generated';
import { useEnvelopeAccountProjections } from './managed-account-overview/hooks/useEnvelopeAccountProjections';
import { useEnvelopePerformanceHistory } from './managed-account-overview/hooks/useEnvelopePerformanceHistory';
import { ManagedAccountOverview } from './managed-account-overview/ManagedAccountOverview';

import { ThemedImage } from '../../../Shared/components-v2/themed-image/ThemedImage';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { AccountSettingsModal } from '../settings/AccountSettingsModal';

export const OverviewCard = () => {
  const accountId = useRequiredParam('accountId');

  const { data } = useGraphQLQuery(EnvelopeAccountHeaderDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  useEnvelopeAccountProjections();
  useEnvelopePerformanceHistory();

  if (!data) return <CardLoader />;
  if (!data.account || !('name' in data.account)) return null;

  const { account } = data;
  const purpose = account.envelope?.purpose;

  const overviewPerformanceAndProjection = () => {
    switch (account.__typename) {
      case 'ManagedAccount':
        return <ManagedAccountOverview />;
      case 'CashAccount':
        return (
          <CashAccountOverview
            apy={account.apy}
            balance={account.balance}
            fdicInsurance={account.fdicInsurance}
            performanceTotals={account.performanceTotals}
            legacyGoalId={account.legacyGoalId}
          />
        );
      case 'DirectTradingAccount':
        return <DirectTradingAccountOverview />;
      default:
        return null;
    }
  };

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      {purpose && (
        <Card.Header>
          <SambaAutoLink to={urlBuilder.appPurpose.absolute({ purposeId: purpose.id })}>
            <Box css={{ display: 'flex', alignItems: 'center', color: 'text' }}>
              <Icon name="CaretLeft" size="small" css={{ mr: 'space.100' }} />
              <Text>{purpose.name}</Text>
            </Box>
          </SambaAutoLink>
        </Card.Header>
      )}
      <Card.Content css={{ gap: 'space.300', display: 'grid' }}>
        <Box css={{ display: 'flex', gap: 'space.200', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box css={{ display: 'flex', gap: 'space.150' }}>
            <ThemedImage
              themedAsset={account.icon}
              alt={account.name}
              css={{ width: 'size.450', height: 'size.450' }}
            />
            <Heading as="h3" variant="title300">
              {account.name}
            </Heading>
          </Box>
          <AccountSettingsModal />
        </Box>
        {overviewPerformanceAndProjection()}
      </Card.Content>
    </Card>
  );
};
